/* eslint max-lines: ["error", {max: 380}] -- Due to the nature of the component */
/* eslint max-lines-per-function: ["error", {max: 310}] -- complex API endpoint with multiple mutation handlers */
import { AppointmentCancellationReason } from "@/constants/Schedule";
import api, { CacheIdType, CacheTagType } from "@/store/api";
import {
  buildFilterByParams,
  getAppointmentsOptions,
  getGroupClassEventsOptions,
  getGroupSectionEventsOptions,
  updateGroupClassEventsCallback,
} from "@/utils/api/schedule/schedule.util";
import {
  groupCombinedEventsByDate,
  groupSectionEventToGroupClassEvent,
} from "@/utils/schedule.util";

interface CancelGCEventRequest {
  groupClassEventExternalId: string;
  patientExternalId?: string;
}

interface CancelGCEventResponse {
  data?: string;
  status?: string;
}

interface CancelAppointmentWithSubReasonRequest {
  appointmentExternalId: string;
  cancellationSubReason: string;
  patientExternalId?: string;
  shouldCancelAll: boolean;
}

interface CancelAppointmentWithRescheduledAppointmentRequest {
  appointmentExternalId: string;
  isAppUserPatient: boolean;
  patientExternalId?: string;
  rescheduleAppointmentExternalId: string;
  shouldCancelAll: boolean;
}

interface CancelAppointmentWithRescheduledAppointmentBody {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  all: boolean;
  appointmentExternalId: string;
  cancellationReason: AppointmentCancellationReason;
  rescheduleAppointmentExternalId: string;
}

interface CancelAppointmentResponse {
  data?: string;
  status?: string;
}

interface GetCancellationSubReasonsResponse {
  data: AppointmentCancellationSubReason[];
}

const GetScheduleEventsEndpoint = "getScheduleEvents";

const scheduleApi = api.injectEndpoints({
  endpoints: (build) => ({
    cancelAppointmentWithRescheduledAppointment: build.mutation<
      CancelAppointmentResponse,
      CancelAppointmentWithRescheduledAppointmentRequest
    >({
      // eslint-disable-next-line max-params
      invalidatesTags: (_result, _error, arg) => {
        const tagId = arg.patientExternalId || CacheIdType.AppUser;
        return [
          { id: CacheIdType.AppUser, type: CacheTagType.AppointmentEvents },
          { id: tagId, type: CacheTagType.AppointmentEvents },
        ];
      },
      /* eslint-disable-next-line max-params */
      onQueryStarted: async (_arg, { dispatch: _dispatch, queryFulfilled }) => {
        await queryFulfilled;
      },
      query: ({
        appointmentExternalId,
        isAppUserPatient,
        rescheduleAppointmentExternalId,
        shouldCancelAll,
      }) => {
        const body: CancelAppointmentWithRescheduledAppointmentBody = {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          all: shouldCancelAll || false,
          appointmentExternalId,
          cancellationReason: isAppUserPatient
            ? AppointmentCancellationReason.PatientReschedule
            : AppointmentCancellationReason.SupportReschedule,
          rescheduleAppointmentExternalId,
        };
        return {
          body,
          method: "PATCH",
          url: "/schedule/family/appointment",
        };
      },
    }),
    cancelAppointmentWithSubReason: build.mutation<
      CancelAppointmentResponse,
      CancelAppointmentWithSubReasonRequest
    >({
      // eslint-disable-next-line max-params
      invalidatesTags: (_result, _error, arg) => {
        const tagId = arg.patientExternalId || CacheIdType.AppUser;
        return [
          { id: CacheIdType.AppUser, type: CacheTagType.AppointmentEvents },
          { id: tagId, type: CacheTagType.AppointmentEvents },
        ];
      },
      /* eslint-disable-next-line max-params */
      onQueryStarted: async (_arg, { dispatch: _dispatch, queryFulfilled }) => {
        await queryFulfilled;
      },
      query: ({
        appointmentExternalId,
        cancellationSubReason,
        shouldCancelAll,
      }) => ({
        body: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          all: shouldCancelAll || false,
          appointmentExternalId,
          cancellationReason: AppointmentCancellationReason.User,
          cancellationSubReason,
        },
        method: "PATCH",
        url: "/schedule/family/appointment",
      }),
    }),
    cancelGroupClassEvent: build.mutation<
      CancelGCEventResponse,
      CancelGCEventRequest
    >({
      // eslint-disable-next-line max-params
      invalidatesTags: (_result, _error, arg) => {
        const tagId = arg.patientExternalId || CacheIdType.AppUser;
        return [{ id: tagId, type: CacheTagType.GroupClassEvents }];
      },
      /* eslint-disable-next-line max-params */
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(
          scheduleApi.util.updateQueryData(
            GetScheduleEventsEndpoint,
            {
              isPastEvents: false,
              pageNumber: 1,
              pageSize: 30,
              patientExternalId: arg.patientExternalId,
            },
            (oldCache: GroupedCombinedScheduleDataListType[]) =>
              updateGroupClassEventsCallback({
                groupClassEventExternalId: arg.groupClassEventExternalId,
                scheduleData: oldCache,
              }),
          ),
        );
      },
      query: ({ groupClassEventExternalId, patientExternalId }) => ({
        body: { patientExternalId },
        method: "POST",
        url: `/schedule/family/group-class-events/${groupClassEventExternalId}/decline`,
      }),
    }),
    getAppointment: build.query<AppointmentDetailResponse, string>({
      query: (appointmentExternalId) =>
        `/schedule/family/appointment-details/${appointmentExternalId}`,
    }),
    getAppointments: build.query<
      GetAppointmentEventsResponse,
      ScheduleEventsQueryParams
    >({
      query: ({
        appointmentTypes,
        isPastEvents,
        isPatient,
        pageNumber,
        pageSize,
        patientExternalId,
      }) => {
        return getAppointmentsOptions({
          appointmentTypes,
          isPastEvents,
          isPatient,
          pageNumber,
          pageSize,
          patientExternalId,
        });
      },
    }),
    getAppointmentsWithOptions: build.query<
      GetAppointmentEventsResponse,
      GetAppointmentsWithOptionsRequest
    >({
      query: ({
        appointmentTypes,
        endDate,
        isPatient,
        pageNumber,
        pageSize,
        patientExternalId,
        sortOrder,
        startDate,
      }) => {
        const params = new URLSearchParams();
        if (pageSize) params.append("page-size", pageSize.toString());
        if (pageNumber) params.append("page-number", pageNumber.toString());
        if (patientExternalId && !isPatient) {
          params.append("patient-id", patientExternalId);
        }
        if (appointmentTypes) {
          params.append("appointment-types", appointmentTypes.join(","));
        }
        if (sortOrder === "DESC") {
          params.append("sort-order", "DESC");
        }
        params.append("filter-by", buildFilterByParams({ endDate, startDate }));

        return { params, url: "/schedule/family/appointment-details" };
      },
    }),
    getCancellationSubReasons: build.query<
      GetCancellationSubReasonsResponse,
      string
    >({
      query: (cancellationReason) =>
        `/schedule/family/appointment/cancellation-reasons/${cancellationReason}/sub-reasons`,
    }),
    getGroupClassEvent: build.query<GroupClassEventResponse, string>({
      query: (groupClassEventExternalId) =>
        `/schedule/family/group-class-events/${groupClassEventExternalId}`,
    }),
    getGroupClassEvents: build.query<
      GetGroupClassEventsResponse,
      ScheduleEventsQueryParams
    >({
      query: ({
        isPastEvents,
        isPatient,
        pageNumber,
        pageSize,
        patientExternalId,
      }) => {
        return getGroupClassEventsOptions({
          isPastEvents,
          isPatient,
          pageNumber,
          pageSize,
          patientExternalId,
        });
      },
    }),
    // v2 upcoming group section events, adapted into the v1 GetGroupClassEventsResponse
    // shape so the combine/card/decline logic downstream is unchanged
    getGroupSectionEvents: build.query<
      GetGroupClassEventsResponse,
      ScheduleEventsQueryParams
    >({
      query: (params) => getGroupSectionEventsOptions(params),
      /* eslint-disable-next-line max-params */
      transformResponse: (
        response: GroupSectionEventResponse[] | undefined,
        _meta,
        arg,
      ): GetGroupClassEventsResponse => ({
        count: response?.length ?? 0,
        data: {
          data: (response ?? []).map(groupSectionEventToGroupClassEvent),
        },
        pageNumber: arg.pageNumber,
        pageSize: arg.pageSize,
        status: "success",
      }),
    }),
    [GetScheduleEventsEndpoint]: build.query<
      GroupedCombinedScheduleDataListType[],
      ScheduleEventsQueryParams
    >({
      // eslint-disable-next-line max-params
      providesTags: (_result, _error, arg) => {
        const tagId = arg.patientExternalId || CacheIdType.AppUser;
        return [
          { id: tagId, type: CacheTagType.AppointmentEvents },
          { id: tagId, type: CacheTagType.GroupClassEvents },
        ];
      },
      /* eslint-disable-next-line max-params */
      queryFn: async (
        arg,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        { dispatch, forced },
      ): Promise<{ data: GroupedCombinedScheduleDataListType[] }> => {
        // if forceRefetch: forced is not passed, an actual refetch will NEVER happen
        // as by default endpoint.initiate(arg) will return a cached response
        // if this value of arg has already been used in invoking endpoint.initiate()

        const shouldSkipGroupClassQuery =
          arg.shouldSkipGroupClassQuery ?? false;
        const emptyGroupClassEventsResponse: GetGroupClassEventsResponse = {
          count: 0,
          data: {
            data: [],
          },
          pageNumber: arg.pageNumber,
          pageSize: arg.pageSize,
          status: "success",
        };
        // behind the redesign flag, group events come from the v2 group-section-events
        // endpoint (adapted to the v1 shape); otherwise the legacy group-class-events
        const groupEventsEndpoint = arg.isV2GroupsEnabled
          ? scheduleApi.endpoints.getGroupSectionEvents
          : scheduleApi.endpoints.getGroupClassEvents;
        const [appointments, groupClassEvents] = await Promise.all([
          dispatch(
            scheduleApi.endpoints.getAppointments.initiate(arg, {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              forceRefetch: forced,
            }),
          ),
          shouldSkipGroupClassQuery
            ? Promise.resolve({ data: emptyGroupClassEventsResponse })
            : dispatch(
                groupEventsEndpoint.initiate(arg, {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  forceRefetch: forced,
                }),
              ),
        ]);
        const combinedData: CombinedScheduleEventsResponse = {
          appointmentEvents: appointments.data,
          classEvents: groupClassEvents.data,
        };
        const groupedEvents = groupCombinedEventsByDate({
          combinedEvents: combinedData,
        });
        return { data: groupedEvents };
      },
    }),
  }),
});

export type { GetCancellationSubReasonsResponse };

export const {
  useCancelAppointmentWithRescheduledAppointmentMutation,
  useCancelAppointmentWithSubReasonMutation,
  useCancelGroupClassEventMutation,
  useGetAppointmentQuery,
  useGetCancellationSubReasonsQuery,
  useGetGroupClassEventQuery,
  useGetScheduleEventsQuery,
  useLazyGetAppointmentsWithOptionsQuery,
} = scheduleApi;
